import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Represents a custom input date component.
 *
 * The debounce time is used to delay emitting the `inputChange` event
 * after the input value has changed. It allows the user to enter the desired
 * date manually without triggering the event for every keystroke.
 *
 */

@Component({
  selector: 'app-input-date',
  templateUrl: './input-date.component.html',
  styleUrls: ['./input-date.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => InputDateComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateComponent implements OnInit {

  @Input() input;
  @Input() type: 'date' | 'year' | 'month' = 'date';
  @Input() inputID = (Math.random() + 1).toString(36).substring(7);
  @Input() title = '';
  @Input() prefixFirstImgSrc = '';
  @Input() prefixSecondImgSrc = '';
  @Input() suffixFirstImgSrc = '';
  @Input() suffixSecondImgSrc = '';
  @Input() firstSuffixImageTooltip = '';
  @Input() secondSuffixImageTooltip = '';
  @Input() bottomText = '';
  @Input() placeholder = '';
  @Input() additionalClass: 'disabled' | 'error' | '' = '';
  @Input() additionalContainerClass = 'control-container';
  @Input() disabled = false;
  @Input() fontClass = '';
  @Input() minDate: string = DEFAULT_DATE_RANGE.minDate;
  @Input() maxDate: string = DEFAULT_DATE_RANGE.maxDate;
  @Input() isRequired = false; // indicates whether the input should be marked with "*"
  @Input() isShrinked = false;
  @Output() inputChange = new EventEmitter<string>();
  @Output() blurEmitter = new EventEmitter<string>();

  private inputChangeSubject: Subject<string> = new Subject<string>();
  /**
   * The `debounceTimeSubject` is used to dynamically adjust the debounce time for input values.
   * It allows for delaying emissions to avoid immediate output for incorrect input conditions, e.g. "0002-mm-dd". <br>
   *
   * The `switchMap` operator in constructor is employed to set the debounce time to 0 milliseconds or 1000 milliseconds,
   * depending on whether the input value is valid or not using the `isDateValid` function.
   */
  private debounceTimeSubject: Subject<number> = new Subject<number>();

  constructor(private cdr: ChangeDetectorRef) {
    this.debounceTimeSubject.pipe(
      switchMap((debounceTimeMS) =>
        this.inputChangeSubject.pipe(
          debounceTime(debounceTimeMS),
          distinctUntilChanged(),
        ),
      ),
    ).subscribe((value) => {
      this.inputChange.emit(value);
    });
  }

  ngOnInit(): void {
    this.setDateRange();
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  writeValue(value: any): void {
    this.input = value;
    this.cdr.detectChanges();
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.additionalClass = isDisabled ? 'disabled' : '';
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(value): void { }

  onTouched(): void { }

  /**
   * Emits the value by input change => [(input)].
   * There are checks if input starts with zero or separator provides extra commas.
   * Also returns the result value.
   *
   * @param value is the raw string.
   */
  onInputChange(value: string): string {
    this.setDebounceTime(value);
    this.inputChangeSubject.next(value);
    this.onChange(value);
    this.inputChange.emit(value);
    return value;
  }

  private setDebounceTime(dateInput: string): void {
    const debounceTimeMS = this.isDateValid(dateInput) ? 0 : INPUT_DEBOUNCE_TIME_MS;
    this.debounceTimeSubject.next(debounceTimeMS);
  }

  private isDateValid(dateInput: string): boolean {
    const dateFormatRegex = this.setRegEx();
    const year = Number(dateInput.substring(0, 4));
    return dateFormatRegex.test(dateInput) && year >= 1970 && year <= 2030;
  }

  private setRegEx(): RegExp {
    switch (this.type) {
      case 'date':
        return /^\d{4}-\d{2}-\d{2}$/;
      case 'month':
        return /^\d{4}-\d{2}$/;
      case 'year':
        return /^\d{4}$/;
      default:
        return /^\d{4}-\d{2}-\d{2}$/;
    }
  }

  private setDateRange(): void {
    switch (this.type) {
      case 'date':
        break;
      case 'month':
        this.minDate = this.minDate ?? DEFAULT_DATE_RANGE.minDate.substring(0, 7);
        this.maxDate = this.maxDate ?? DEFAULT_DATE_RANGE.maxDate.substring(0, 7);
        break;
      case 'year':
        this.minDate = this.minDate ?? DEFAULT_DATE_RANGE.minDate.substring(0, 4);
        this.maxDate = this.maxDate ?? DEFAULT_DATE_RANGE.maxDate.substring(0, 4);
        break;
      default:
        break;
    }
  }
}

const INPUT_DEBOUNCE_TIME_MS = 1000;

export const DEFAULT_DATE_RANGE = {
  minDate: '1970-01-01',
  maxDate: '2030-12-31',
};

